import { useFilters } from '@/modules/accredited-establishments/hooks'
import { WTextfield } from '@alice-health/wonderland-react'
import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { WTextfieldCustomEvent } from '@alice-health/wonderland'
import debounce from 'lodash/debounce'

interface ProviderNameSearchFieldProps {
  /** Collapses the field when it loses focus while empty */
  onEmptyBlur?: () => void
}

const ProviderNameSearchField: FunctionComponent<
  ProviderNameSearchFieldProps
> = ({ onEmptyBlur }) => {
  const { providerName, updateProviderName, ready } = useFilters()

  const [search, setSearch] = useState<string>('')
  const [initializedSearch, setInitializedSearch] = useState<boolean>(false)

  const fieldRef = useRef<HTMLWTextfieldElement>(null)

  /*
   * O campo monta quando alguem abre a busca, entao o cursor tem que ir para
   * ele. O `autoFocus` do `WTextfield` chega ao input interno como atributo, e
   * o navegador ja passou do ponto de honra-lo quando o web component hidrata.
   */
  useEffect(() => {
    let active = true

    const focusInput = async () => {
      const field = fieldRef.current

      /* Sem o design system carregado o elemento nao hidrata e nao tem o metodo. */
      await field?.componentOnReady?.()

      if (!active) return

      field?.querySelector('input')?.focus()
    }

    focusInput()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (ready && !initializedSearch) {
      setInitializedSearch(true)
      setSearch(providerName || '')
    }
  }, [ready, initializedSearch, providerName])

  const debouncedUpdate = useRef(
    debounce((value: string) => {
      updateProviderName(value)
    }, 300)
  ).current

  /*
   * Closing the mobile search clears the term and unmounts the field. A call
   * still in flight would reapply the old term with no field left to show it.
   */
  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate])

  /*
   * The term can also be cleared from outside the field — `Limpar busca`, in the
   * empty state, does it. Only the clear direction syncs back: mirroring every
   * value would let a debounced update overwrite what is being typed.
   */
  useEffect(() => {
    if (!initializedSearch || providerName) return

    debouncedUpdate.cancel()
    setSearch('')
  }, [providerName, initializedSearch, debouncedUpdate])

  const onChange = (event: WTextfieldCustomEvent<string>) => {
    const value = event.detail

    setSearch(value)

    if (value.length > 2 || value.length === 0) {
      debouncedUpdate(value)
    }
  }

  /* With an active search the field stays: the text is the only filter cue. */
  const onBlur = () => {
    if (!search) {
      onEmptyBlur?.()
    }
  }

  return (
    <WTextfield
      ref={fieldRef}
      value={search}
      placeholder='Buscar por nome'
      trailingIcon='icSearch'
      onWChange={onChange}
      onWBlur={onBlur}
    />
  )
}

export { ProviderNameSearchField }
export type { ProviderNameSearchFieldProps }
